/**
 * Get the application version
 * 
 * Format: yyyy.m.d.ssss
 * - yyyy = year
 * - m = month (1-12)
 * - d = day (1-31)
 * - ssss = seconds since midnight UTC
 * 
 * Example: 2026.1.29.45678 = January 29, 2026 at 12:41:18 UTC
 * 
 * This matches the backend version format.
 */
export function getAppVersion(): string {
  return import.meta.env.VITE_APP_VERSION || 'dev';
}

/**
 * Parse the version string into components
 */
export function parseVersion(version: string): {
  year: number;
  month: number;
  day: number;
  secondsSinceMidnight: number;
  timestamp: Date;
} | null {
  const parts = version.split('.');
  if (parts.length !== 4) return null;

  const [year, month, day, seconds] = parts.map(Number);
  
  // Create a UTC date from the version
  const timestamp = new Date(Date.UTC(year, month - 1, day));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  timestamp.setUTCHours(hours, minutes, secs);

  return {
    year,
    month,
    day,
    secondsSinceMidnight: seconds,
    timestamp,
  };
}

/**
 * Format version as a human-readable string
 */
export function formatVersion(version: string): string {
  const parsed = parseVersion(version);
  if (!parsed) return version;

  const { timestamp } = parsed;
  return timestamp.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

